import React, { useState, useEffect } from 'react'
import { Link, useSearchParams, useNavigate } from 'react-router-dom'
import { Button } from '@/components/atoms/Button'
import { Input } from '@/components/atoms/Input'
import { MortiseMark } from '@/components/atoms/MortiseMark'

export default function ResetPasswordPage() {
  useEffect(() => {
    document.title = "Tenon — Reset Password"
    return () => { document.title = "Tenon" }
  }, [])

  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const token = searchParams.get('token') || ''

  const [password, setPassword] = useState('')
  const [confirm, setConfirm] = useState('')
  const [status, setStatus] = useState<'validating' | 'valid' | 'invalid' | 'loading' | 'done' | 'error'>('validating')
  const [errorMsg, setErrorMsg] = useState('')

  useEffect(() => {
    if (!token) { setStatus('invalid'); return }
    fetch(`/api/v1/auth/reset-password?token=${encodeURIComponent(token)}`)
      .then(r => r.ok ? setStatus('valid') : setStatus('invalid'))
      .catch(() => setStatus('invalid'))
  }, [token])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (password.length < 8) { setErrorMsg('Password must be at least 8 characters'); return }
    if (password !== confirm) { setErrorMsg('Passwords do not match'); return }
    setStatus('loading')
    setErrorMsg('')
    try {
      const res = await fetch('/api/v1/auth/reset-password', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, password }),
      })
      if (!res.ok) {
        const j = await res.json().catch(() => ({}))
        throw new Error((j as any).message || 'Reset failed')
      }
      setStatus('done')
      setTimeout(() => navigate('/login'), 2500)
    } catch (err: unknown) {
      setStatus('error')
      setErrorMsg(err instanceof Error ? err.message : 'Reset failed')
    }
  }

  return (
    <div style={{
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      background: 'var(--bg-page)',
      fontFamily: "'Karla', -apple-system, sans-serif",
      paddingTop: 'calc(24px + env(safe-area-inset-top, 0px))',
      paddingBottom: 'calc(24px + env(safe-area-inset-bottom, 0px))',
      paddingLeft: '16px',
      paddingRight: '16px',
      position: 'relative',
    }}>
      {/* Gold gradient ambient */}
      <div style={{
        position: 'fixed', inset: 0, pointerEvents: 'none', zIndex: 0,
        background: 'radial-gradient(80% 60% at 100% 0%, color-mix(in oklch, var(--brand-gold) 10%, transparent), transparent 70%)',
      }} />

      <div style={{ width: '100%', maxWidth: 440, display: 'flex', flexDirection: 'column', alignItems: 'center', position: 'relative', zIndex: 1 }}>

        <a href="https://usetenon.com" style={{ display: 'flex', alignItems: 'center', marginBottom: 32, textDecoration: 'none' }}>
          <MortiseMark size={44} light />
        </a>

        <div style={{
          width: '100%',
          background: 'var(--bg-surface)',
          border: '1px solid var(--border)',
          borderRadius: 12,
          padding: '36px 40px',
          boxShadow: 'var(--shadow-2)',
        }}>
          {/* Eyebrow */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 7, marginBottom: 10 }}>
            <span style={{ width: 5, height: 5, borderRadius: '50%', background: 'var(--brand-gold)', display: 'inline-block', flexShrink: 0 }} />
            <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.14em', textTransform: 'uppercase' as const, color: 'var(--text-muted)', fontFamily: "'Karla', -apple-system, sans-serif" }}>
              Tenon · Password Reset
            </span>
          </div>

          {status === 'validating' && (
            <>
              <h1 style={{ fontSize: '1.625rem', fontWeight: 700, color: 'var(--text-primary)', margin: '0 0 6px', letterSpacing: '-0.025em', lineHeight: 1.1 }}>
                Checking your link
              </h1>
              <p style={{ fontSize: 14, color: 'var(--text-secondary)', marginBottom: 0, marginTop: 0, lineHeight: 1.55 }}>
                Validating…
              </p>
            </>
          )}

          {status === 'invalid' && (
            <>
              <h1 style={{ fontSize: '1.625rem', fontWeight: 700, color: 'var(--text-primary)', margin: '0 0 6px', letterSpacing: '-0.025em', lineHeight: 1.1 }}>
                Link expired
              </h1>
              <p style={{ fontSize: 14, color: 'var(--text-secondary)', marginBottom: 24, marginTop: 0, lineHeight: 1.55 }}>
                This reset link is invalid or has expired. Request a new one.
              </p>
              <div style={{ textAlign: 'center' }}>
                <Link to="/forgot-password" style={{ color: 'var(--text-secondary)', fontSize: 13, textDecoration: 'none', fontWeight: 600 }}>
                  Request a new link →
                </Link>
              </div>
            </>
          )}

          {status === 'done' && (
            <>
              <h1 style={{ fontSize: '1.625rem', fontWeight: 700, color: 'var(--text-primary)', margin: '0 0 6px', letterSpacing: '-0.025em', lineHeight: 1.1 }}>
                Password updated
              </h1>
              <p style={{ fontSize: 14, color: 'var(--text-secondary)', marginBottom: 24, marginTop: 0, lineHeight: 1.55 }}>
                Redirecting you to sign in…
              </p>
              <div style={{
                padding: '16px',
                background: 'color-mix(in oklch, var(--st-active) 8%, transparent)',
                border: '1px solid color-mix(in oklch, var(--st-active) 40%, transparent)',
                borderRadius: 10,
                color: 'var(--st-active)',
                fontSize: 14,
                lineHeight: 1.6,
                textAlign: 'center',
              }}>
                ✓ Password updated successfully.
              </div>
            </>
          )}

          {(status === 'valid' || status === 'loading' || status === 'error') && (
            <>
              <h1 style={{ fontSize: '1.625rem', fontWeight: 700, color: 'var(--text-primary)', margin: '0 0 6px', letterSpacing: '-0.025em', lineHeight: 1.1 }}>
                Set a new password
              </h1>
              <p style={{ fontSize: 14, color: 'var(--text-secondary)', marginBottom: 28, marginTop: 0, lineHeight: 1.55 }}>
                Choose a strong password (8+ characters).
              </p>
              <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column' }}>
                {errorMsg && (
                  <div style={{
                    padding: '10px 14px',
                    background: 'color-mix(in oklch, var(--st-error) 8%, transparent)',
                    border: '1px solid color-mix(in oklch, var(--st-error) 40%, transparent)',
                    borderRadius: 8,
                    color: 'var(--st-error)',
                    fontSize: 13,
                    marginBottom: 16,
                  }}>
                    {errorMsg}
                  </div>
                )}
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 16 }}>
                  <label htmlFor="password" style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-primary)' }}>New password</label>
                  <Input
                    type="password"
                    id="password"
                    placeholder="••••••••"
                    value={password}
                    onChange={e => setPassword(e.target.value)}
                    required
                    autoFocus
                  />
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 24 }}>
                  <label htmlFor="confirm" style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-primary)' }}>Confirm password</label>
                  <Input
                    type="password"
                    id="confirm"
                    placeholder="••••••••"
                    value={confirm}
                    onChange={e => setConfirm(e.target.value)}
                    required
                  />
                </div>
                <Button
                  type="submit"
                  variant="primary"
                  disabled={status === 'loading'}
                  style={{ width: '100%', marginBottom: 20 }}
                >
                  {status === 'loading' ? 'Updating…' : 'Update password'}
                </Button>
                <div style={{ textAlign: 'center' }}>
                  <Link to="/login" style={{ color: 'var(--text-secondary)', fontSize: 13, textDecoration: 'none', fontWeight: 600 }}>
                    ← Back to log in
                  </Link>
                </div>
              </form>
            </>
          )}
        </div>

        {/* Sage callout */}
        <div style={{
          marginTop: 24,
          background: 'color-mix(in oklch, var(--brand-gold) 7%, var(--bg-surface))',
          border: '1px solid color-mix(in oklch, var(--brand-gold) 18%, var(--border-subtle))',
          borderRadius: 10,
          padding: '14px 18px',
          display: 'flex',
          alignItems: 'flex-start',
          gap: 12,
          width: '100%',
        }}>
          <div style={{
            width: 30, height: 30, borderRadius: 7, flexShrink: 0,
            background: 'color-mix(in oklch, var(--brand-gold) 15%, var(--bg-elevated))',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontWeight: 800, fontSize: 12, color: 'var(--brand-gold)',
            fontFamily: "'Karla', -apple-system, sans-serif",
          }}>S</div>
          <p style={{ fontSize: 13, color: 'var(--text-secondary)', lineHeight: 1.55, margin: 0 }}>
            <strong style={{ color: 'var(--text-primary)' }}>Sage is watching your projects.</strong> Log in to see what needs your attention today.
          </p>
        </div>

        <div style={{ marginTop: 20 }}>
          <Link to="/login" style={{ color: 'var(--text-muted)', fontSize: 12, textDecoration: 'none' }}>
            ← Back to usetenon.com
          </Link>
        </div>
      </div>
    </div>
  )
}
